// src/components/Footer.tsx
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram } from 'lucide-react';
import './Footer.css';
import BugReportModal from './BugReportModal';
import { useAuth } from '../context/AuthContext';
import { HELP_CENTER_URL } from '../config/helpCenter';

const Footer: React.FC = () => {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const isLandingPage = location.pathname === '/';
  const isAppRoute = location.pathname.startsWith('/app');

  const handleSectionLink = (sectionId: string) => {
    if (isLandingPage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [isBugModalOpen, setIsBugModalOpen] = useState(false);

  return (
    <footer className={`footer ${isAppRoute ? 'footer-with-sidebar footer--sav-clearance' : ''}`}>
      <div className="footer-container">
        <div className="footer-sections">
          <div className="footer-section footer-section--brand">
            <div className="footer-logo">
              <h3 className="footer-brand">
                Rintintin Pro<span className="footer-brand-dot">.</span>
              </h3>
            </div>
            <p className="footer-tagline">{t('footer.tagline')}</p>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.productHelp')}</h4>
            <ul className="footer-links">
              <li>
                <button
                  type="button"
                  className="footer-link footer-button"
                  onClick={() => handleSectionLink('fonctionnalites')}
                >
                  {t('footer.features')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="footer-link footer-button"
                  onClick={() => handleSectionLink('tarifs')}
                >
                  {t('header.pricing')}
                </button>
              </li>
              <li>
                <a
                  href={HELP_CENTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  {t('header.faq')}
                </a>
              </li>
              <li>
                <Link to="/fonctionnalites" className="footer-link">
                  {t('footer.allFeatures')}
                </Link>
              </li>
              <li>
                <a
                  href={HELP_CENTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  {t('footer.helpCenter')}
                </a>
              </li>
              <li>
                <a
                  href="https://whats-new-rintintin.netlify.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  {t('footer.whatsNew')}
                </a>
              </li>
              <li>
                <Link to="/offre-configuration" className="footer-link">
                  {t('footer.configurationOffer')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="footer-link">
                  {t('footer.blog', { defaultValue: 'Blog & guides' })}
                </Link>
              </li>
              <li>
                <Link to="/partenaires" className="footer-link">
                  {t('footer.partners')}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  className="footer-link footer-button"
                  onClick={() => handleSectionLink('contact')}
                >
                  {t('header.contact')}
                </button>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.legal')}</h4>
            <ul className="footer-links">
              <li>
                <Link to="/terms-of-use" className="footer-link">
                  {t('footer.termsOfUse')}
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="footer-link">
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="footer-link">
                  {t('footer.generalTermsOfSale')}
                </Link>
              </li>
              <li>
                <Link to="/legal" className="footer-link">
                  {t('footer.legalNotice')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.account')}</h4>
            <ul className="footer-links">
              {isAuthenticated ? (
                <li>
                  <button
                    type="button"
                    className="footer-link footer-button"
                    onClick={handleLogout}
                  >
                    {t('footer.logout')}
                  </button>
                </li>
              ) : (
                <>
                  <li>
                    <Link to="/login" className="footer-link">
                      {t('header.login')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/signup-user" className="footer-link">
                      {t('footer.createAccount')}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">
            &copy; {new Date().getFullYear()} Rintintin - {t('footer.allRightsReserved')}
          </p>
          <div className="footer-social">
            <a
              href="https://www.instagram.com/rintintin.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              aria-label="Instagram Rintintin"
            >
              <Instagram size={18} aria-hidden />
            </a>
            <button
              type="button"
              className="footer-bug-btn"
              onClick={() => setIsBugModalOpen(true)}
              aria-label={t('footer.reportBug')}
            >
              {t('footer.reportBug')}
            </button>
          </div>
        </div>
      </div>
      <BugReportModal isOpen={isBugModalOpen} onClose={() => setIsBugModalOpen(false)} />
    </footer>
  );
};

export default Footer;
